/**
 * Field tech job screen — the next-action machine (TECH-JOB-SCREEN.md).
 *
 * Two-column layout on iPad landscape (≥1024px): the workflow rail (left)
 * presents the job's steps in the exact order the tech works them, and the
 * sticky bottom button is always the active step's primary action. The
 * reference column (right) holds everything the tech looks up but never acts
 * on — every fact renders exactly once.
 *
 * Presentation layer only: step derivation lives in ./job-detail/steps.ts and
 * every action fires an endpoint/route that existed before the redesign.
 */
import { JobOutcomeBox } from "@/components/job-outcome-box";
import { phaseOf } from "@/pages/jobs/fresh/freshModel";
import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { needsTestingWorkflow, testingWorkflowPath, type CompletionErrorBody } from "@/lib/testing-workflow-link";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    api,
    ApiError,
    attachmentDownloadUrl,
    attachmentsZipUrl,
    downloadAttachmentFile,
    downloadUrlAsFile,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFieldRedesign } from "@/lib/use-field-redesign";
import { composeJobTitle, doneReceipt, isJobDoneForTech, plainJobTypeName, statusMeaning } from "./redesign-copy";
import { useOfflineSync, queueOfflineAction, useQueuedSubmitJobIds } from "@/lib/offline-sync";
import { openExternalLink } from "@/lib/standalone";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageSpinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { fileToNormalizedDataUrl } from "@/lib/downscale-image";
import { useFileDrop, usePasteFiles } from "@/lib/file-intake";
import { Dialog } from "@/components/ui/dialog";
import { FloatingJobChat } from "@/components/floating-job-chat";
import { TeachIntro } from "@/teach/teach-card";
import { useFieldAddJob } from "@/components/field-add/field-add-api";
import { FieldAddSheet } from "@/components/field-add/field-add-sheet";
import {
    FieldAddButton,
    FieldAddCustomerLine,
    FieldAddDeviceExtras,
    FieldAddLocationCard,
} from "@/components/field-add/field-add-job";
import { FlagProblemButton } from "./flag-problem";
import { PortalEntryWizard } from "@/components/portal-entry-wizard";
import { LeavingSiteCloseoutModal, type LeavingSiteResolution } from "@/components/leaving-site-closeout-modal";
import { useGpsDeparture } from "@/lib/use-gps-departure";
import { useLeftOpen } from "@/components/left-open/left-open-provider";
import { cn, statusColor } from "@/lib/utils";
import { customerWoNumber } from "@/lib/wo-number";
import { formatFieldTime, orgTodayKey } from "@/lib/datetime";
import { toast } from "sonner";
import {
    AlertTriangle,
    ArrowLeft,
    Phone,
    Navigation,
    CheckCircle,
    Play,
    Wrench,
    Camera,
    StickyNote,
    Loader2,
    Briefcase,
    AlertCircle,
    ClipboardCheck,
    XCircle,
    Undo2,
    Upload,
    Flame,
    Trash2,
    LogIn,
    RefreshCw,
    ChevronUp,
} from "lucide-react";
import { buildWorkflowSteps, deriveDeviceStatuses, helperVisibleStatuses, type StepAction } from "./job-detail/steps";
import {
    PartsForJobCard,
    PartsDispositionDialog,
    type GatePart,
    type PartDispositionAnswer,
} from "./job-detail/parts-custody-card";
import { WorkflowRail } from "./job-detail/workflow-rail";
import { buildCallOptions } from "./job-detail/call-options";
import { ContactsCard, useJobContactCard } from "./job-detail/contacts-card";
import { SiteHistoryTab } from "./job-detail/site-history-tab";
import { ReferenceColumn, type ReferenceBriefing, type ReferenceDevice } from "./job-detail/reference-column";
import { ScopeLinesCard } from "./job-detail/scope-lines-card";
import { TechPlanBlock } from "@/components/tech-plan/tech-plan-block";
import { ReadYourPlan, usePlanReads } from "@/components/tech-plan/plan-read";
import { useTechPlan } from "@/lib/tech-plan";
import { isPastedQuoteNote } from "@/lib/scope-lines";
import { LocationTruthCard } from "./job-detail/location-truth-card";
import { OnsiteReportCopySection, type OnsiteReportDisposition } from "./job-detail/onsite-report-copy";
import { JobFilesCard } from "./job-detail/job-files-card";
import {
    photoFiles,
    seenStorageKey,
    markNewFiles,
    type JobFile,
    type JobPaperworkResponse,
} from "./job-detail/job-files";
import { readOfflineReadyIds, warmJobFiles } from "./job-detail/prefetch-job-files";
import { PhotoLightbox } from "@/components/photo-lightbox";

/** Pull the parts list out of a 409 PARTS_DISPOSITION_REQUIRED error. */
function extractPartsGate(err: unknown): GatePart[] | null {
    const e = err as { status?: number; body?: { error?: string; parts?: GatePart[] } };
    if (e?.status === 409 && e?.body?.error === "PARTS_DISPOSITION_REQUIRED" && Array.isArray(e.body.parts)) {
        return e.body.parts;
    }
    return null;
}

/** #1958 — the server's "test it after the repair first" refusal. */
function extractPostRepairTest(err: unknown): { message: string; assetId: string } | null {
    const e = err as {
        status?: number;
        body?: { error?: string; message?: string; devices?: Array<{ assetId: string }> };
    };
    if (e?.status === 409 && e?.body?.error === "POST_REPAIR_TEST_NEEDED" && e.body.devices?.[0]) {
        return { message: e.body.message ?? "Test it after the repair first.", assetId: e.body.devices[0].assetId };
    }
    return null;
}

// ─── Types ────────────────────────────────────────────────────────────

interface JobAsset {
    link_id: string;
    asset_id: string;
    label: string | null;
    size_label: string | null;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    location_description: string | null;
    asset_status: string | null;
    asset_type: string | null;
    assembly_type: string | null;
    service_type: string | null;
    required_action: string | null;
    disposition: string | null;
    sequence_no: number | null;
    parent_asset_id: string | null;
    /** #948 J5 — the work order's own answer to "where is it" (migration 531),
     *  and the read-time flag saying it disagrees with the device record and
     *  nobody has settled it. Both have been in this payload since #819. */
    wo_reported_location?: string | null;
    location_conflict?: boolean | null;
}

interface JobHeader {
    id: string;
    /** "No work order yet" (2026-10-21) — services/jobWorkOrderMissing.ts. */
    wo_missing?: boolean | null;
    display_number: string | null;
    title: string;
    status: string;
    customer_id: string | null;
    company_name: string | null;
    customer_phone: string | null;
    contact_id: string | null;
    contact_first_name: string | null;
    contact_last_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    contact_role: string | null;
    site_id: string | null;
    site_name: string | null;
    site_label: string | null;
    site_address_line: string | null;
    site_address_line2: string | null;
    site_city: string | null;
    site_region: string | null;
    site_postal_code: string | null;
    site_latitude: string | number | null;
    site_longitude: string | number | null;
    site_phone?: string | null;
    /** Legacy ST location code — never shown as the portal account. */
    site_account_number?: string | null;
    /** Canonical portal account (`sites.water_account_number`). */
    site_water_account_number?: string | null;
    site_contact_name?: string | null;
    site_contact_phone?: string | null;
    water_provider_name?: string | null;
    water_provider_display_name?: string | null;
    water_provider_submission_platform?: string | null;
    water_provider_portal_url?: string | null;
    water_provider_is_portal?: boolean | null;
    /** §3f: jurisdiction allows the tech to send a report copy from site. */
    water_provider_allow_onsite_report_send?: boolean | null;
    /** §3f: recorded wrap-up disposition (gates Complete Job when the flag is on). */
    onsite_report_disposition?: OnsiteReportDisposition | null;
    legacy_address_line: string | null;
    trade_name: string | null;
    trade_id: string | null;
    job_type_name: string | null;
    job_type_code: string | null;
    notes: string | null;
    tech_instructions: string | null;
    /** Office-authored on-site brief (jobs.summary, Markdown) — pinned to the
     *  top of the tech reference column. */
    summary: string | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    customer_notes_scheduling: string | null;
    cannot_complete_reason: string | null;
    arrived_at: string | null;
    work_started_at: string | null;
    completed_at: string | null;
    /** Stamped when the tech submits the backflow / fire-safety test report
     *  (Review & Submit). Gates "Complete Job" — submitting a report no
     *  longer closes the job. */
    test_submitted_at?: string | null;
    /** Set by office "Ask Tech to Close Out" on the missed-jobs modal.
     *  When non-null, the field job-detail screen pops the closeout
     *  modal automatically and shows the office's note at the top. */
    pending_closeout_at: string | null;
    pending_closeout_note: string | null;
    pending_closeout_by_name: string | null;
    kind?: string | null;
    // Vitals row (2026-07-14): PO/WO reference in the header (customer_phone
    // already lives on the base header fields above).
    customer_po?: string | null;
    external_ref?: string | null;
    briefing_wo_number?: string | null;
    work_order_briefing?: WorkOrderBriefing | null;
    ivr_checkin_at?: string | null;
    ivr_checkin_confirmation?: string | null;
    ivr_checkout_at?: string | null;
    ivr_checkout_confirmation?: string | null;
}

interface WorkOrderBriefing {
    work_order_number?: string | null;
    scope_of_work?: string | null;
    special_instructions?: string | null;
    site_contact_name?: string | null;
    site_contact_phone?: string | null;
    ivr_phone?: string | null;
    ivr_pin?: string | null;
    tracking_number?: string | null;
    confirmation_number?: string | null;
}

interface JobDetailResponse {
    job: JobHeader;
    devices: unknown[];
    assets: JobAsset[];
    /** §3f item 4: [Text it] appears only when org SMS is live (A2P approved). */
    onsiteReportSmsEnabled?: boolean;
}

/** One entry of the job notes thread (GET /api/jobs/:id/notes). */
interface JobNoteEntry {
    id: string;
    author_user_id: string | null;
    author_name: string | null;
    body: string;
    pinned: boolean;
    created_at: string;
}

/** Subset of GET /api/jobs/:id/wo-details the screen needs (tech notes for
 *  the amber warning + scope fallback). */
interface WoDetails {
    jobId: string;
    ivrNumber: string | null;
    ivrPhone: string | null;
    techNotes: string | null;
    scopeOfWork: string | null;
}

interface TestResult {
    test_id: string;
    asset_id: string;
    overall_result: string;
    /** Helper Mode v2: who originally typed the row (null = the tech). */
    entered_by_user_id?: string | null;
    entered_by_name?: string | null;
    /** Helper Mode v2: set once the tech edit-saved or tapped [Looks right]. */
    reviewed_at?: string | null;
}

// ─── Photo chooser (camera or gallery) ───────────────────────────────

function dataUrlToBlob(dataUrl: string): Blob {
    const comma = dataUrl.indexOf(",");
    const meta = dataUrl.slice(5, comma);
    const mime = meta.split(";")[0] || "image/jpeg";
    const bin = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

async function uploadJobPhotoFromDataUrl(jobId: string, dataUrl: string, photoTag?: string | null): Promise<void> {
    const blob = dataUrlToBlob(dataUrl);
    const ext = blob.type.includes("png") ? "png" : "jpg";
    const file = new File([blob], `job-photo.${ext}`, { type: blob.type });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "photo");
    fd.append("jobId", jobId);
    if (photoTag) fd.append("photoTag", photoTag);
    await api.postForm("/api/attachments/upload", fd);
}

function JobPhotoChooser({
    onPhoto,
    onCancel,
    multi,
}: {
    onPhoto: (dataUrl: string) => void;
    onCancel: () => void;
    /**
     * GLOBAL PHOTO RULE: job photos are a multi-photo gallery — the library
     * picker accepts N photos in one go (each fires onPhoto). Retakes of a
     * specific photo leave this unset and stay locked to one image.
     */
    multi?: boolean;
}) {
    const uploadRef = useRef<HTMLInputElement>(null);
    const cameraRef = useRef<HTMLInputElement>(null);

    const acceptFiles = useCallback(
        async (incoming: File[] | FileList | undefined | null) => {
            const files = Array.from(incoming ?? []).filter((f) => f.type.startsWith("image/"));
            const wanted = multi ? files : files.slice(0, 1);
            for (const file of wanted) {
                try {
                    onPhoto(await fileToNormalizedDataUrl(file));
                } catch {
                    /* ignore unreadable file */
                }
            }
        },
        [onPhoto, multi],
    );

    const handleFile = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            e.target.value = "";
            await acceptFiles(files);
        },
        [acceptFiles],
    );

    // Universal drag & drop item 2: this chooser also accepts dropped or
    // pasted images (iPad split-screen drag from Photos, desktop screenshots).
    const { dragging, dropProps } = useFileDrop((files) => void acceptFiles(files));
    usePasteFiles((files) => void acceptFiles(files));

    return (
        <div
            className={cn(
                "space-y-3 py-2 rounded-lg transition-colors",
                dragging && "bg-primary/10 ring-2 ring-primary/50",
            )}
            {...dropProps}
        >
            <p className="text-center text-xs text-foreground/60">
                {multi
                    ? "Pick several at once, drop or paste images here, or use the buttons"
                    : "Drop or paste an image here, or use the buttons"}
            </p>
            <input
                ref={uploadRef}
                type="file"
                accept="image/*"
                multiple={multi}
                className="hidden"
                onChange={handleFile}
            />
            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFile}
            />
            <Button className="w-full h-14 gap-3 text-base" onClick={() => cameraRef.current?.click()}>
                <Camera className="h-5 w-5" /> Take Photo
            </Button>
            <Button
                variant="outline"
                className="w-full h-14 gap-3 text-base"
                onClick={() => uploadRef.current?.click()}
            >
                <Upload className="h-5 w-5" /> Upload from Gallery
            </Button>
            <Button variant="ghost" className="w-full" onClick={onCancel}>
                Cancel
            </Button>
        </div>
    );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function formatTime(iso: string): string {
    // Use the shared org-display-zone formatter so field screens show the same
    // wall-clock time as dispatch/office (honors VITE_FIELD_TIMEZONE).
    return formatFieldTime(iso);
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function buildAddress(job: JobHeader): string {
    const parts = [
        job.site_address_line || job.legacy_address_line,
        job.site_address_line2,
        [job.site_city, job.site_region].filter(Boolean).join(", "),
        job.site_postal_code,
    ];
    return parts.filter(Boolean).join(", ");
}

function buildMapsUrl(job: JobHeader): string | null {
    const lat = job.site_latitude ? Number(job.site_latitude) : null;
    const lng = job.site_longitude ? Number(job.site_longitude) : null;
    if (lat && lng) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    const addr = buildAddress(job);
    if (!addr) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Icon for each sticky-button action kind. */
const ACTION_ICONS: Record<StepAction["kind"], typeof Play> = {
    "on-my-way": Navigation,
    "start-work": Play,
    "ivr-checkin": Phone,
    "ivr-checkout": Phone,
    "open-backflow": Wrench,
    "open-fire-safety": Flame,
    "open-wo": ClipboardCheck,
    "complete-job": CheckCircle,
};

// ─── GPS proximity hook ───────────────────────────────────────────────

function useGpsProximity(siteLat: number | null, siteLng: number | null, enabled: boolean, thresholdMeters = 100) {
    const [isNearby, setIsNearby] = useState(false);
    const [distance, setDistance] = useState<number | null>(null);
    const nearbyStartRef = useRef<number | null>(null);

    useEffect(() => {
        if (!enabled || siteLat == null || siteLng == null) {
            setIsNearby(false);
            setDistance(null);
            nearbyStartRef.current = null;
            return;
        }
        if (!("geolocation" in navigator)) return;

        const id = navigator.geolocation.watchPosition(
            (pos) => {
                const d = haversineMeters(pos.coords.latitude, pos.coords.longitude, siteLat, siteLng);
                setDistance(Math.round(d));
                if (d <= thresholdMeters) {
                    if (!nearbyStartRef.current) nearbyStartRef.current = Date.now();
                    if (Date.now() - nearbyStartRef.current >= 30_000) {
                        setIsNearby(true);
                    }
                } else {
                    nearbyStartRef.current = null;
                    setIsNearby(false);
                }
            },
            () => {},
            { enableHighAccuracy: true, maximumAge: 15_000, timeout: 10_000 },
        );
        return () => navigator.geolocation.clearWatch(id);
    }, [enabled, siteLat, siteLng, thresholdMeters]);

    return { isNearby, distance };
}

// ─── Component ────────────────────────────────────────────────────────

export default function FieldJobDetailPage() {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    // Inside a job, "back" must always escape to the tech home screen. Using
    // history-based back (navigate(-1)) trapped techs in a loop between job
    // detail and the just-finished workflow after completing a test.
    const goHome = () => navigate("/field");
    const qc = useQueryClient();
    // #948 — fail-closed redesign flag (#821). Off renders today's job page.
    const { enabled: redesignEnabled } = useFieldRedesign();
    const { isOnline } = useOfflineSync();
    /** #1512 — jobs whose submit is still on this phone. */
    const queuedSubmitJobIds = useQueuedSubmitJobIds();
    const { user } = useAuth();
    // Helper Mode: the ride-along helper sees the same rail + reference but
    // the lifecycle bottom button becomes THEIR button (Clock In / Clock Out).
    // On My Way / Complete / Can't Complete stay the tech's acts.
    const isHelper = user?.is_field_helper === true;

    const [showCompletionSummary, setShowCompletionSummary] = useState(false);
    const [showCannotComplete, setShowCannotComplete] = useState(false);
    // Parts Custody gate: when a closeout 409s with PARTS_DISPOSITION_REQUIRED,
    // this holds the parts to answer for + the retry that resubmits with the
    // answers attached.
    const [partsGate, setPartsGate] = useState<{
        parts: GatePart[];
        retry: (answers: PartDispositionAnswer[]) => void;
    } | null>(null);
    const [cannotCompleteReason, setCannotCompleteReason] = useState("");
    // General job notes: techs add notes any time during the job (not only at
    // closeout) into the existing append-only /notes thread.
    const [showAddNote, setShowAddNote] = useState(false);
    const [noteDraft, setNoteDraft] = useState("");
    const [showJobPhotos, setShowJobPhotos] = useState(false);
    const [retakeTarget, setRetakeTarget] = useState<JobFile | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [previewAttachment, setPreviewAttachment] = useState<{ id: string; fileName: string } | null>(null);
    const [gpsAutoArrivalDismissed, setGpsAutoArrivalDismissed] = useState(false);
    // §3e: Job (the working screen, default) vs Site History (everything on
    // file for this site across all past jobs). The Job tab is CSS-hidden,
    // not unmounted, so workflow state survives a browse through history.
    const [activeTab, setActiveTab] = useState<"job" | "history">("job");

    // IVR confirmation capture. window.prompt is broken inside the iOS Safari
    // standalone PWA (it silently returns null), so we collect the number in a
    // proper modal instead.
    const [ivrDialog, setIvrDialog] = useState<{ action: "checkin" | "checkout" } | null>(null);
    const [ivrConfirmation, setIvrConfirmation] = useState("");

    // ─── Closeout modal state ─────────────────────────────────────────
    // Open the LeavingSiteCloseoutModal when:
    //   1. useGpsDeparture fires (tech moved outside the site geofence
    //      with the job still in arrived/in_progress), or
    //   2. URL has ?openCloseout=1 (push deep-link from request-closeout), or
    //   3. job.pending_closeout_at is set (office asked tech to close).
    const [closeoutOpen, setCloseoutOpen] = useState(false);
    const [closeoutTrigger, setCloseoutTrigger] = useState<"geofence" | "office_request">("geofence");
    /** Throttle the geofence "snooze" so dismissing doesn't immediately
     *  re-pop. 30 minutes — long enough for a real "I'll come back". */
    const closeoutSnoozeUntilRef = useRef<number>(0);
    const [searchParamsForCloseout, setSearchParamsForCloseout] = useState<URLSearchParams | null>(null);
    useEffect(() => {
        // Capture the deep-link param once, then strip it so navigating
        // around inside the job (or refreshing) doesn't keep re-opening
        // the modal after it's already been dealt with.
        if (typeof window !== "undefined") {
            const params = new URLSearchParams(window.location.search);
            setSearchParamsForCloseout(params);
        }
    }, []);

    // Add from the field (behind field_add; off = nothing renders).
    const fieldAddQ = useFieldAddJob(jobId);
    const fieldAdd = fieldAddQ.data?.on === true ? fieldAddQ.data : null;
    const [fieldAddOpen, setFieldAddOpen] = useState(false);
    const [fieldAddPreset, setFieldAddPreset] = useState<string | null>(null);

    const detail = useQuery({
        queryKey: ["field", "job-detail", jobId],
        queryFn: () => api.get<JobDetailResponse>(`/api/jobs/${jobId}`),
        enabled: !!jobId,
    });

    // Every live file on this job in ONE call (Issue #719). This replaced six
    // per-category `/api/attachments?category=…` queries that between them
    // never asked for `document`, so the job's own original work order — which
    // lands as a document with a provenance note — was invisible here and only
    // reachable through the Site History tab, buried among the whole site's
    // files. The server classifies each row (what it is, who added it), so the
    // card, the photo grid and the report-copy gating all read one source.
    const paperwork = useQuery({
        queryKey: ["field", "job-paperwork", jobId],
        queryFn: () => api.get<JobPaperworkResponse>(`/api/field/jobs/${jobId}/paperwork`),
        enabled: !!jobId,
        staleTime: 30_000,
    });
    const jobFiles = paperwork.data?.files ?? [];
    const jobPhotos = photoFiles(jobFiles);
    const hasCompletedWorkOrder = jobFiles.some((f) => f.kind === "completed_work_order");
    const lightboxPhoto = lightboxIndex != null ? (jobPhotos[lightboxIndex] ?? null) : null;

    // Hold the work orders on the device while we still have signal, so the
    // paper the job hangs off still opens in a basement. The service worker
    // already CacheFirsts attachment downloads; this just warms it and then
    // reports back what is genuinely cached (never claim more than that).
    const [offlineReadyIds, setOfflineReadyIds] = useState<Set<string>>(new Set());
    const filesKey = jobFiles.map((f) => f.id).join(",");
    useEffect(() => {
        let cancelled = false;
        if (!jobFiles.length) return;
        void (async () => {
            if (isOnline) await warmJobFiles(jobFiles);
            const ready = await readOfflineReadyIds(jobFiles);
            if (!cancelled) setOfflineReadyIds(ready);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesKey, isOnline]);

    // "New since you last looked" — local only. `reviewed_at` on the server
    // belongs to the office's WO-review flow and must not be overloaded here.
    const [filesLastSeen, setFilesLastSeen] = useState<string | null>(null);
    useEffect(() => {
        if (!jobId) return;
        try {
            setFilesLastSeen(localStorage.getItem(seenStorageKey(jobId)));
            localStorage.setItem(seenStorageKey(jobId), new Date().toISOString());
        } catch {
            /* private mode / blocked storage — no dots, no crash */
        }
    }, [jobId]);
    const newFileIds = markNewFiles(jobFiles, filesLastSeen);

    const job = detail.data?.job;
    const assets = detail.data?.assets ?? [];

    const isBackflow = job?.trade_name?.toLowerCase().includes("backflow") ?? false;

    // Fire safety covers both extinguishers and e-lights — branch off
    // the trade name OR the job_type_code prefix so a custom org-defined
    // job type (e.g. "fire_safety_annual") still routes correctly.
    const tradeName = job?.trade_name?.toLowerCase() ?? "";
    const jobTypeCode = job?.job_type_code ?? "";
    const isFireSafety =
        /fire ext|fire safety|extinguisher/.test(tradeName) ||
        jobTypeCode.startsWith("fire_ext_") ||
        jobTypeCode.startsWith("fire_safety_");

    const bfResults = useQuery({
        queryKey: ["backflow", "job-results", jobId],
        queryFn: () => api.get<{ results: TestResult[] }>(`/api/backflow/job/${jobId}/results`),
        enabled: isBackflow && !!jobId,
    });

    // Fire-safety inspection results (one row per inspected device).
    // Drives the live status chips in the reference-column device list.
    const fsResults = useQuery({
        queryKey: ["fire-safety", "job-results", jobId],
        queryFn: () =>
            api.get<{ results: { asset_id: string; result: "pass" | "fail" }[] }>(
                `/api/fire-safety/job/${jobId}/results`,
            ),
        enabled: isFireSafety && !!jobId,
    });

    const isRepairJob = job?.job_type_code?.includes("repair") ?? false;

    const bfRepairs = useQuery({
        queryKey: ["backflow", "job", jobId, "repairs"],
        queryFn: () => api.get<{ repairs: { asset_id: string; id: string }[] }>(`/api/backflow/job/${jobId}/repairs`),
        enabled: isBackflow && isRepairJob && !!jobId,
    });

    // Vendor WO gating inputs: the WO completion form's submitted_at marker
    // and the IVR requirement. Both feed the step engine so "Complete Job"
    // stays locked until every WO step is actually done.
    const isVendorWO = job?.kind === "vendor_work_order";
    const woCompletionQ = useQuery({
        queryKey: ["field", "wo-completion", jobId],
        queryFn: () =>
            api.get<{ completion: { submitted_at?: string | null } | null }>(
                `/api/jobs/${jobId}/work-order-completion`,
            ),
        enabled: isVendorWO && !!jobId,
    });
    const woConfigQ = useQuery({
        queryKey: ["field", "wo-config", jobId],
        queryFn: () => api.get<{ config: { has_ivr?: boolean } | null }>(`/api/jobs/${jobId}/work-order-config`),
        enabled: isVendorWO && !!jobId,
    });
    // Amber scheduling / tech notes + scope fallback for the briefing card.
    const woDetailsQ = useQuery({
        queryKey: ["field", "wo-details", jobId],
        queryFn: () => api.get<WoDetails>(`/api/jobs/${jobId}/wo-details`),
        enabled: isVendorWO && !!jobId,
    });

    // Job notes thread — the brief renders the full thread (newest first,
    // pinned on top) instead of the old single jobs.notes string. Vendor
    // WOs carry their notes inside the briefing scope, so skip there.
    const jobNotesQ = useQuery({
        queryKey: ["job-notes", jobId],
        queryFn: () => api.get<{ notes: JobNoteEntry[] }>(`/api/jobs/${jobId}/notes`),
        enabled: !isVendorWO && !!jobId && !!job,
    });

    // Tech two-contact card (CONTACTS-SUBCONTRACT-LAYER.md Prompt E): the
    // resolved on-site person + coordinator. Feeds the KNOW-column Contacts
    // card AND Get There's "Call site" so both always dial the same person.
    const contactCardQ = useJobContactCard(jobId);

    // Job messages unread badge — same channel + unread queries FloatingJobChat
    // uses, so the counts stay in sync via the shared cache.
    const jobChannel = useQuery({
        queryKey: ["chat", "job-channel", jobId],
        queryFn: () => api.get<{ channelId: string }>(`/api/chat/job/${jobId}/channel`),
        enabled: !!jobId,
        staleTime: 300_000,
    });
    const channelId = jobChannel.data?.channelId;
    const chatUnread = useQuery({
        queryKey: ["chat", "unread-job", channelId],
        queryFn: () => api.get<{ total: number }>(`/api/chat/unread-total?channelId=${channelId}`),
        enabled: !!channelId,
        refetchInterval: 30000,
    });

    const hasReplaceAssets = assets.some((a) => a.required_action === "replace");
    const bfReplacements = useQuery({
        queryKey: ["backflow", "job", jobId, "replacements"],
        queryFn: () =>
            api.get<{
                replacements: {
                    id: string;
                    old_asset_id: string;
                    finalized_at: string | null;
                    pair_role: string | null;
                }[];
            }>(`/api/backflow/job/${jobId}/replacements`),
        enabled: isBackflow && !!jobId && hasReplaceAssets,
    });

    /** Returns true when the given asset has a saved test/inspection result for
     *  this job, regardless of trade. A DCDA/RPDA bypass child inherits its
     *  parent's test (the bypass was tested with the main). */
    const isAssetTested = (assetId: string): boolean => {
        if (isFireSafety) {
            return fsResults.data?.results?.some((r) => r.asset_id === assetId) ?? false;
        }
        if (bfResults.data?.results?.some((r) => r.asset_id === assetId)) return true;
        // A bypass leg (parent_asset_id set) is tested with its detector
        // parent — the bypass readings live on the parent's
        // backflow_test_results row (bypass_*_psid), so the child never gets
        // its own result. Inherit purely from the parent link; do NOT gate on
        // the parent's assembly_type, which imports routinely leave blank and
        // which used to strand a tested bypass as "not tested" — blocking
        // Complete Job on a fully-tested DCDA/RPDA.
        const asset = assets.find((a) => a.asset_id === assetId);
        if (asset?.parent_asset_id && bfResults.data?.results?.some((r) => r.asset_id === asset.parent_asset_id)) {
            return true;
        }
        return false;
    };

    const totalAssets = assets.length;
    const replaceAssets = assets.filter((a) => a.required_action === "replace");
    const testableCount = totalAssets - replaceAssets.length;
    // A device counts as resolved once it has a saved test OR an explicit
    // release disposition (skipped / not_due / not_located / could_not_test).
    // Without the disposition check, a phantom device a tech "couldn't test"
    // keeps "Complete Job" disabled until someone deletes the device.
    const testedCount = isFireSafety
        ? (fsResults.data?.results?.length ?? 0)
        : assets.filter(
              (a) => a.required_action !== "replace" && (isAssetTested(a.asset_id) || (a.disposition ?? null) != null),
          ).length;
    const repairAssets = assets.filter((a) => a.required_action === "repair");
    const repairCount = repairAssets.length;
    const repairedCount = repairAssets.filter((a) =>
        bfRepairs.data?.repairs?.some((r) => r.asset_id === a.asset_id),
    ).length;

    // Replacement progress — only main-pair rows count toward the per-asset
    // total so a detector/bypass swap on the same parent doesn't double-count.
    const replaceCount = replaceAssets.length;
    const replacedCount = replaceAssets.filter((a) =>
        bfReplacements.data?.replacements?.some(
            (r) =>
                r.old_asset_id === a.asset_id &&
                r.finalized_at != null &&
                (r.pair_role == null || r.pair_role === "main"),
        ),
    ).length;

    // ─── Step engine — derive the rail + sticky action ────────────────
    const briefing = job?.work_order_briefing ?? {};
    // 👷 What the tech will do (2026-10-22): the office's block, first on the
    // screen, in their exact words. Until it loads (or offline) the old scope
    // card and the WO notes stand in, so the tech is never left with nothing.
    const techPlanQ = useTechPlan(jobId, { asTech: true });
    const techPlan = techPlanQ.data?.audience === "tech" ? techPlanQ.data.plan : null;
    // threads/job-page-fresh: a line the office changed stays marked until his
    // Got it on "Read your plan" (#2017) after the change.
    const planReadsQ = usePlanReads(jobId);
    const myRead = planReadsQ.data?.mine ?? null;
    const planReadAt = myRead && myRead.verdict === "got_it" && !myRead.stale ? myRead.created_at : null;
    const woHasIvr =
        isVendorWO &&
        (woConfigQ.data?.config?.has_ivr === true || Boolean(briefing.ivr_phone) || Boolean(woDetailsQ.data?.ivrPhone));

    // Helper Mode v2 review gate: helper-entered test rows the tech hasn't
    // reviewed yet (edit-save or [Looks right] in the testing workflow).
    const helperUnreviewedCount = (bfResults.data?.results ?? []).filter(
        (r) => r.entered_by_user_id != null && r.reviewed_at == null,
    ).length;
    const helperEnteredByName =
        (bfResults.data?.results ?? []).find((r) => r.entered_by_user_id != null && r.reviewed_at == null)
            ?.entered_by_name || "your helper";

    const engine = buildWorkflowSteps({
        status: job?.status ?? "",
        isVendorWO,
        isBackflow,
        isFireSafety,
        isRepairJob,
        hasIvr: woHasIvr,
        workStartedAt: job?.work_started_at ?? null,
        ivrCheckinAt: job?.ivr_checkin_at ?? null,
        ivrCheckinConfirmation: job?.ivr_checkin_confirmation ?? null,
        ivrCheckoutAt: job?.ivr_checkout_at ?? null,
        ivrCheckoutConfirmation: job?.ivr_checkout_confirmation ?? null,
        testSubmittedAt: job?.test_submitted_at ?? null,
        woSubmittedAt: woCompletionQ.data?.completion?.submitted_at ?? null,
        testableCount,
        resolvedCount: testedCount,
        repairCount,
        repairedCount,
        replaceCount,
        replacedCount,
        formatTime,
        // Helper Mode v2: the engine strips tech-only actions and unlocks the
        // input steps for helpers; the review gate holds Complete Job.
        viewerIsHelper: isHelper,
        helperUnreviewedCount,
    });
    // Auto-scroll the newly-active step into view when a step completes.
    // Ref-tracked previous value so the initial render never jumps.
    const prevActiveStepRef = useRef<string | null>(null);
    useEffect(() => {
        const prev = prevActiveStepRef.current;
        prevActiveStepRef.current = engine.activeStepId;
        if (prev === null || prev === engine.activeStepId || engine.activeStepId === null) return;
        document.getElementById(`step-${engine.activeStepId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [engine.activeStepId]);

    const testComplete = testableCount === 0 || testedCount >= testableCount;
    // #1512 — the tech submitted the report; it is on this phone on its way to
    // the office. `job.test_submitted_at` is the SERVER's marker and cannot
    // exist yet, so gating Complete Job on it alone left an offline tech who
    // had done everything staring at a locked button — the same dead end as
    // the outbox-gated submit, one screen later. The queued submit is the
    // local truth until the server has its copy.
    const submitQueuedHere = !!jobId && queuedSubmitJobIds.includes(jobId);
    const reportSubmitted =
        (!isBackflow && !isFireSafety) ||
        testableCount === 0 ||
        (job?.test_submitted_at ?? null) != null ||
        submitQueuedHere;
    const woFormSubmitted = !isVendorWO || (woCompletionQ.data?.completion?.submitted_at ?? null) != null;
    const repairComplete = !isRepairJob || repairCount === 0 || repairedCount >= repairCount;
    const replaceComplete = replaceCount === 0 || replacedCount >= replaceCount;

    // "General" workflow = no trade-specific inspection workflow at all
    // (service call, plumbing repair, etc.). Drives wrap-up copy so we
    // don't ask the tech to attest to inspections that never existed.
    const isGeneralWorkflow = !isBackflow && !isFireSafety;

    // §3f — jurisdiction-gated on-site report copy. Only jobs whose water
    // provider carries the flag (City of Plano) require a wrap-up
    // disposition before Complete Job; everywhere else reports stay
    // withheld until invoiced (server-enforced 403 on the send endpoint).
    const [recordedReportDisposition, setRecordedReportDisposition] = useState<OnsiteReportDisposition | null>(null);
    const reportDisposition = recordedReportDisposition ?? job?.onsite_report_disposition ?? null;
    const reportCopyRequired =
        !isHelper && job?.water_provider_allow_onsite_report_send === true && !isGeneralWorkflow && testableCount > 0;

    const siteLat = job?.site_latitude ? Number(job.site_latitude) : null;
    const siteLng = job?.site_longitude ? Number(job.site_longitude) : null;
    const isEnRoute = job?.status === "en_route";
    const { isNearby } = useGpsProximity(siteLat, siteLng, isEnRoute && !gpsAutoArrivalDismissed && !isHelper);

    // GPS auto-start-work notification
    useEffect(() => {
        if (isNearby && isEnRoute && !gpsAutoArrivalDismissed && !isHelper) {
            toast("You appear to be on site", {
                description: "Ready to start work?",
                action: {
                    label: "Start Work",
                    onClick: () => advanceStatus.mutate({ status: "in_progress" }),
                },
                duration: 15000,
                onDismiss: () => setGpsAutoArrivalDismissed(true),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNearby, isEnRoute]);

    // ─── GPS departure detection ─────────────────────────────────────
    // Only watch when:
    //   - the job is still in arrived/in_progress (not yet closed out),
    //   - we know they actually got on-site (arrived_at is set),
    //   - the modal isn't already open / snoozed.
    const closeoutEligibleStatus = job?.status === "arrived" || job?.status === "in_progress";
    const closeoutSnoozed = Date.now() < closeoutSnoozeUntilRef.current;
    // No job left open: with the switch on, the app-shell watcher owns the
    // departure check and the question (no Hide); this screen's old modal
    // stays for everyone else.
    const leftOpen = useLeftOpen();
    useGpsDeparture({
        siteLat,
        siteLng,
        enabled:
            !leftOpen.on &&
            !!job &&
            closeoutEligibleStatus &&
            !!job?.arrived_at &&
            !closeoutOpen &&
            !closeoutSnoozed &&
            !isHelper,
        onDeparture: ({ distanceM, lat, lng }) => {
            // Audit-only ping — server logs that the tech left the
            // geofence without closing. Does NOT change state — the
            // modal handles the actual user-facing decision.
            if (jobId) {
                api.post(`/api/jobs/${jobId}/departure-detected`, {
                    distanceM,
                    lat,
                    lng,
                    statusAtDeparture: job?.status ?? null,
                }).catch(() => {});
            }
            setCloseoutTrigger("geofence");
            setCloseoutOpen(true);
        },
    });

    // ─── Auto-open from office request / push deep-link ───────────────
    useEffect(() => {
        if (!job) return;
        if (closeoutOpen) return;
        // Close-out is the tech's decision — never prompt a helper.
        if (isHelper) return;
        // Office "Ask Tech to Close Out" sets pending_closeout_at.
        if (job.pending_closeout_at && closeoutEligibleStatus) {
            if (jobId && leftOpen.openPrompt(jobId, "office_request")) return;
            setCloseoutTrigger("office_request");
            setCloseoutOpen(true);
            return;
        }
        // Push deep-link: /field/jobs/:id?openCloseout=1 — strip the
        // param after consuming it so refreshes don't re-open.
        if (searchParamsForCloseout?.get("openCloseout") === "1") {
            if (!(jobId && leftOpen.openPrompt(jobId, "push"))) {
                setCloseoutTrigger(job.pending_closeout_at ? "office_request" : "geofence");
                setCloseoutOpen(true);
            }
            try {
                const url = new URL(window.location.href);
                url.searchParams.delete("openCloseout");
                window.history.replaceState({}, "", url.toString());
            } catch {
                /* noop */
            }
            setSearchParamsForCloseout(null);
        }
    }, [job, closeoutEligibleStatus, closeoutOpen, searchParamsForCloseout, isHelper, jobId, leftOpen]);

    const invalidateJob = useCallback(() => {
        qc.invalidateQueries({ queryKey: ["field", "job-detail", jobId] });
        qc.invalidateQueries({ queryKey: ["field", "jobs"] });
    }, [qc, jobId]);

    // ─── Helper Mode: clock in/out session (HELPER-MODE.md §2) ─────────
    const helperSessionQ = useQuery({
        queryKey: ["field", "helper-session", jobId],
        queryFn: () =>
            api.get<{ session: { id: string; clockInAt: string; clockOutAt: string | null } | null }>(
                `/api/jobs/${jobId}/helper-session`,
            ),
        enabled: isHelper && !!jobId,
    });
    const helperSession = helperSessionQ.data?.session ?? null;
    const helperClockedIn = helperSession != null && helperSession.clockOutAt == null;

    const helperClock = useMutation({
        mutationFn: async (action: "in" | "out") => {
            // Geolocation is best-effort — a dead GPS or denied permission
            // must never block the clock action (HELPER-MODE.md default #3).
            const coords = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
                if (!("geolocation" in navigator)) {
                    resolve(null);
                    return;
                }
                navigator.geolocation.getCurrentPosition(
                    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    () => resolve(null),
                    { enableHighAccuracy: false, maximumAge: 60_000, timeout: 5_000 },
                );
            });
            return api.post(`/api/jobs/${jobId}/helper-clock-${action}`, coords ?? {});
        },
        onSuccess: (_data, action) => {
            toast.success(action === "in" ? "Clocked in — you're on this job" : "Clocked out");
            qc.invalidateQueries({ queryKey: ["field", "helper-session", jobId] });
        },
        onError: () => toast.error("Could not update your clock status"),
    });

    const advanceStatus = useMutation({
        mutationFn: async ({ endpoint, status }: { endpoint?: string; status?: string }) => {
            if (!jobId) return;
            const url = endpoint ? `/api/jobs/${jobId}/${endpoint}` : `/api/jobs/${jobId}/status`;
            // #1513 — stamp WHEN HE TAPPED, not when the request lands. A
            // queued Complete Job from a dead zone can arrive the next
            // morning, and payroll and points bucket the tech's day off
            // jobs.completed_at. Captured here, before the offline branch, so
            // the queued copy carries the same moment the online one would.
            const body = endpoint ? undefined : { status, occurredAt: new Date().toISOString() };
            if (!isOnline) {
                // #1512 — hold it behind this job's own queue. "Complete Job"
                // is queued AFTER the test submit, but without this marker the
                // drain could still replay it first (a held submit is skipped,
                // an unheld status change is not) — and the server 422s a
                // completion whose report hasn't arrived
                // (WORKFLOW_INCOMPLETE / report_not_submitted), which is a
                // semantic rejection and would PARK the tech's close as a
                // permanent "didn't sync".
                await queueOfflineAction({ url, method: "POST", body: body ?? {}, holdForJobId: jobId });
                toast.info("Saved — it'll go out with the rest of this job's paperwork.");
                return;
            }
            return api.post(url, body);
        },
        onSuccess: invalidateJob,
        onError: (err) => {
            const e = err as { status?: number; body?: CompletionErrorBody & { error?: string; message?: string } };
            if (e?.status === 409 && e?.body?.error === "JOB_NOT_REVIEW_READY") {
                toast.error(
                    e.body.message || "This job needs office setup (jurisdiction/account number) before you can start.",
                );
                return;
            }
            // #1601 #4 — the server's own sentence, never a bare "Could not
            // update status"; and when the testing isn't finished, the button
            // that goes there.
            const message = e?.body?.message || "Could not update status";
            if (jobId && needsTestingWorkflow(e?.body)) {
                toast.error(message, {
                    action: { label: "Finish Review & Submit", onClick: () => navigate(testingWorkflowPath(jobId)) },
                });
                return;
            }
            toast.error(message);
        },
    });

    const ivrCheckin = useMutation({
        mutationFn: (confirmation: string | null) => api.post(`/api/jobs/${jobId}/ivr-checkin`, { confirmation }),
        onSuccess: () => {
            toast.success("IVR check-in recorded");
            qc.invalidateQueries({ queryKey: ["field", "job-detail", jobId] });
        },
        onError: () => toast.error("Could not record IVR check-in"),
    });
    const ivrCheckout = useMutation({
        mutationFn: (confirmation: string | null) => api.post(`/api/jobs/${jobId}/ivr-checkout`, { confirmation }),
        onSuccess: () => {
            toast.success("IVR check-out recorded");
            qc.invalidateQueries({ queryKey: ["field", "job-detail", jobId] });
        },
        onError: () => toast.error("Could not record IVR check-out"),
    });

    const openIvrDialog = (action: "checkin" | "checkout") => {
        setIvrConfirmation("");
        setIvrDialog({ action });
    };

    // Issue #454: the tech's NON-terminal "return needed" — ends the trip and
    // hands the job to the office, which decides add-another-appointment vs
    // cannot-complete. The tech never sets the terminal cannot_complete status.
    const returnNeeded = useMutation({
        mutationFn: async ({
            reason,
            kind,
            partDispositions,
        }: {
            reason: string;
            kind: "couldnt_start" | "couldnt_finish";
            partDispositions?: PartDispositionAnswer[];
        }) => {
            if (!jobId) return;
            if (!isOnline) {
                await queueOfflineAction({
                    url: `/api/jobs/${jobId}/return-needed`,
                    method: "POST",
                    body: { reason, kind },
                });
                toast.info("Queued for sync");
                return;
            }
            return api.post(`/api/jobs/${jobId}/return-needed`, { reason, kind, partDispositions });
        },
        onSuccess: () => {
            setPartsGate(null);
            invalidateJob();
            setShowCannotComplete(false);
            toast.success("Sent to office");
            navigate("/field");
        },
        onError: (err: unknown, vars) => {
            const gateParts = extractPartsGate(err);
            if (gateParts) {
                setShowCannotComplete(false);
                setPartsGate({
                    parts: gateParts,
                    retry: (answers) =>
                        returnNeeded.mutate({ reason: vars.reason, kind: vars.kind, partDispositions: answers }),
                });
                return;
            }
            // #1958: a repaired device needs its test after the repair first.
            // Open the full test form on it; the readings (0 is fine) save a
            // real FAILED test, and then he can send it back.
            const retest = extractPostRepairTest(err);
            if (retest) {
                setShowCannotComplete(false);
                toast.error(retest.message, { duration: 9000 });
                navigate(`/field/backflow/${jobId}?step=retest&asset=${retest.assetId}`);
                return;
            }
            toast.error("Could not send to office. Please try again.");
        },
    });

    // Photos-card "Add photo" (the rail). The wrap-up modal's separate photo
    // path was removed in §3c — this stays for the main Photos card.
    const uploadPhoto = useMutation({
        mutationFn: async ({ dataUrl, photoTag }: { dataUrl: string; photoTag?: string | null }) => {
            if (!jobId) return;
            if (!isOnline) {
                // Queue as a multipart file upload (not JSON) so the replay
                // matches the multipart-only /api/attachments/upload contract.
                // Queuing JSON here used to 400 on reconnect and silently
                // drop the photo.
                await queueOfflineAction({
                    url: `/api/attachments/upload`,
                    method: "POST",
                    body: null,
                    fileUpload: {
                        fileField: "file",
                        dataUrl,
                        fileName: `job-photo.${dataUrl.includes("image/png") ? "png" : "jpg"}`,
                        fields: { category: "photo", jobId, photoTag: photoTag ?? null },
                    },
                });
                return;
            }
            await uploadJobPhotoFromDataUrl(jobId, dataUrl, photoTag);
        },
        onSuccess: () => {
            setShowJobPhotos(false);
            setRetakeTarget(null);
            qc.invalidateQueries({ queryKey: ["field", "job-paperwork", jobId] });
            toast.success("Photo uploaded");
        },
        onError: (e: unknown) => {
            const err = e as { body?: { message?: string; error?: string }; message?: string };
            const message = err?.body?.message ?? err?.body?.error ?? err?.message ?? "unknown";
            toast.error(`Could not upload photo: ${message}`);
        },
    });

    // "Add file" on the Job Files card (Issue #719): a tech can attach the
    // paperwork they're handed on site — a signed form, a permit, a vendor's
    // sign-off — instead of routing it through the office.
    //
    // IMAGES GO TO category='photo' WITH NO photo_tag, ON PURPOSE. A
    // `document` row that has a photo_tag AND an image mime type matches the
    // signature-privacy fingerprint (server: attachmentPrivacy.ts) and would be
    // filtered out of every list surface, office and field, permanently.
    const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // multer's cap in attachments.ts
    const uploadFiles = useMutation({
        mutationFn: async (picked: File[]) => {
            if (!jobId) return;
            const tooBig = picked.find((f) => f.size > MAX_UPLOAD_BYTES);
            if (tooBig) {
                throw new Error(`"${tooBig.name}" is over the 10 MB limit. Try a smaller scan or photo.`);
            }
            for (const file of picked) {
                const isImage = file.type.startsWith("image/");
                const category = isImage ? "photo" : "document";
                if (!isOnline) {
                    const dataUrl = isImage
                        ? await fileToNormalizedDataUrl(file)
                        : await new Promise<string>((resolve, reject) => {
                              const reader = new FileReader();
                              reader.onload = () => resolve(String(reader.result));
                              reader.onerror = () => reject(reader.error);
                              reader.readAsDataURL(file);
                          });
                    await queueOfflineAction({
                        url: `/api/attachments/upload`,
                        method: "POST",
                        body: null,
                        fileUpload: {
                            fileField: "file",
                            dataUrl,
                            fileName: file.name,
                            fields: { category, jobId, notes: isImage ? null : "Added by the tech on the job screen" },
                        },
                    });
                    continue;
                }
                const form = new FormData();
                form.append("file", file);
                form.append("jobId", jobId);
                form.append("category", category);
                if (!isImage) form.append("notes", "Added by the tech on the job screen");
                await api.postForm(`/api/attachments/upload`, form);
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["field", "job-paperwork", jobId] });
            toast.success(isOnline ? "File added" : "File saved — it'll upload when you're back on signal");
        },
        onError: (e: unknown) => {
            const err = e as { body?: { message?: string; error?: string }; message?: string };
            const message = err?.body?.message ?? err?.body?.error ?? err?.message ?? "unknown";
            toast.error(`Could not add the file: ${message}`);
        },
    });

    const downloadAll = useMutation({
        mutationFn: (ids: string[]) => downloadUrlAsFile(attachmentsZipUrl(ids), `job-files-${orgTodayKey()}.zip`),
        onError: () => toast.error("Could not download the files"),
    });

    const deletePhoto = useMutation({
        mutationFn: async (attachmentId: string) => {
            if (!isOnline) {
                await queueOfflineAction({
                    url: `/api/attachments/${attachmentId}`,
                    method: "DELETE",
                    body: null,
                });
                return;
            }
            await api.delete(`/api/attachments/${attachmentId}`);
        },
        onSuccess: () => {
            setLightboxIndex(null);
            qc.invalidateQueries({ queryKey: ["field", "job-paperwork", jobId] });
            toast.success("Photo deleted");
        },
        onError: () => {
            toast.error("Could not delete photo");
        },
    });

    const retakePhoto = useMutation({
        mutationFn: async ({ attachment, dataUrl }: { attachment: JobFile; dataUrl: string }) => {
            if (!jobId) return;
            if (!isOnline) {
                await queueOfflineAction({
                    url: `/api/attachments/${attachment.id}`,
                    method: "DELETE",
                    body: null,
                });
                await queueOfflineAction({
                    url: `/api/attachments/upload`,
                    method: "POST",
                    body: null,
                    fileUpload: {
                        fileField: "file",
                        dataUrl,
                        fileName: `job-photo.${dataUrl.includes("image/png") ? "png" : "jpg"}`,
                        fields: { category: "photo", jobId, photoTag: attachment.photoTag ?? null },
                    },
                });
                return;
            }
            await api.delete(`/api/attachments/${attachment.id}`);
            await uploadJobPhotoFromDataUrl(jobId, dataUrl, attachment.photoTag);
        },
        onSuccess: () => {
            setRetakeTarget(null);
            setLightboxIndex(null);
            qc.invalidateQueries({ queryKey: ["field", "job-paperwork", jobId] });
            toast.success("Photo updated");
        },
        onError: () => {
            toast.error("Could not retake photo");
        },
    });

    // Surface server completion-gate failures that would otherwise be silent
    // (e.g. WORKFLOW_INCOMPLETE when a device still needs testing, or
    // ASSET_DATA_INCOMPLETE when required asset fields are missing). Without
    // this the wrap-up modal just sat open with no feedback.
    const handleCompletionError = useCallback((err: unknown) => {
        const e = err as { status?: number; body?: { error?: string; message?: string } };
        const code = e?.body?.error;
        if (
            e?.body?.message &&
            (code === "WORKFLOW_INCOMPLETE" || code === "ASSET_DATA_INCOMPLETE" || code === "MISSING_CUSTOMER_WO")
        ) {
            // MISSING_CUSTOMER_WO (#887): this account pays against their WO
            // number — the office adds it on the job Details card, then the
            // close-out is retried. The server message says exactly that.
            toast.error(e.body.message);
            return;
        }
        toast.error(e?.body?.message || "Could not complete the job. Please try again.");
    }, []);

    // Wrap Up "Mark complete" (§3c): moves the job to office_review. Notes are
    // no longer collected here — techs add them to the job notes thread from
    // the job screen as they work (Issue #282). The old "Get signature" path
    // is gone too — customer sign-off happens in the WO wizard / test
    // workflows, and its /api/jobs/:id/signature endpoint never existed
    // server-side.
    const submitCompletion = useMutation({
        mutationFn: async (partDispositions?: PartDispositionAnswer[]) => {
            if (!jobId) return;
            if (!isOnline) {
                await queueOfflineAction({
                    url: `/api/jobs/${jobId}/status`,
                    method: "POST",
                    body: { status: "office_review" },
                });
                toast.info("Completion queued for sync");
                return;
            }
            return api.post(`/api/jobs/${jobId}/status`, { status: "office_review", partDispositions });
        },
        onSuccess: () => {
            setPartsGate(null);
            setShowCompletionSummary(false);
            invalidateJob();
            // Refresh the tech's active-jobs list so the field home can detect
            // "last job of the day done" and auto-surface the parts-to-stage prompt.
            qc.invalidateQueries({ queryKey: ["field", "jobs"] });
            toast.success("Sent to office review");
            navigate("/field");
        },
        onError: (err: unknown) => {
            const gateParts = extractPartsGate(err);
            if (gateParts) {
                setShowCompletionSummary(false);
                setPartsGate({
                    parts: gateParts,
                    retry: (answers) => submitCompletion.mutate(answers),
                });
                return;
            }
            handleCompletionError(err);
        },
    });

    // General job note (Issue #282): a tech drops a note into the append-only
    // /notes thread any time during the job. Offline-queued like every other
    // field write so it survives a dead mechanical-room signal.
    const addNote = useMutation({
        mutationFn: async () => {
            const body = noteDraft.trim();
            if (!jobId || !body) return;
            if (!isOnline) {
                await queueOfflineAction({
                    url: `/api/jobs/${jobId}/notes`,
                    method: "POST",
                    body: { notes: body },
                });
                toast.info("Note queued for sync");
                return;
            }
            return api.post(`/api/jobs/${jobId}/notes`, { notes: body });
        },
        onSuccess: () => {
            setShowAddNote(false);
            setNoteDraft("");
            qc.invalidateQueries({ queryKey: ["job-notes", jobId] });
            toast.success("Note added");
        },
        onError: () => {
            toast.error("Could not add the note. Please try again.");
        },
    });

    const handleCloseoutResolve = useCallback(
        (res: LeavingSiteResolution) => {
            switch (res.kind) {
                case "complete":
                    setCloseoutOpen(false);
                    setShowCompletionSummary(true);
                    return;
                case "returnNeeded":
                    // Issue #454: both "couldn't start" and "couldn't finish" are
                    // non-terminal — end the trip and hand it to the office.
                    setCloseoutOpen(false);
                    returnNeeded.mutate({ reason: res.reason, kind: res.startKind });
                    return;
                case "notYet":
                    closeoutSnoozeUntilRef.current = Date.now() + 30 * 60_000;
                    setCloseoutOpen(false);
                    toast("We'll remind you again in 30 minutes", {
                        description: "If you don't close it out, the office will be alerted.",
                    });
                    return;
            }
        },
        [returnNeeded],
    );

    const handleCloseoutDismiss = useCallback(() => {
        // For office-request triggers, the banner stays on the field
        // home; the office will keep nagging via the persistent banner
        // until terminal status is set. For geofence, snooze 30 min.
        closeoutSnoozeUntilRef.current = Date.now() + 30 * 60_000;
        setCloseoutOpen(false);
    }, []);

    // ─── Sticky button / rail action dispatch — same endpoints as before ──
    const handleAction = useCallback(
        (action: StepAction) => {
            // Helper Mode: lifecycle + IVR are the tech's acts (server 403s
            // them too). Workflow navigation stays open — helpers enter data.
            if (
                isHelper &&
                ["on-my-way", "start-work", "ivr-checkin", "ivr-checkout", "complete-job"].includes(action.kind)
            ) {
                toast.info("That's your tech's step — your saved work goes to them for review.");
                return;
            }
            switch (action.kind) {
                case "on-my-way":
                    if (jobId && leftOpen.guardOnMyWay(jobId, job?.site_name || null)) return;
                    advanceStatus.mutate({ endpoint: "on-my-way" });
                    return;
                case "start-work":
                    advanceStatus.mutate({ status: "in_progress" });
                    return;
                case "ivr-checkin":
                    openIvrDialog("checkin");
                    return;
                case "ivr-checkout":
                    openIvrDialog("checkout");
                    return;
                case "open-backflow":
                    navigate(`/field/backflow/${jobId}`);
                    return;
                case "open-fire-safety":
                    navigate(`/field/fire-safety/${jobId}`);
                    return;
                case "open-wo":
                    navigate(`/field/work-order/${jobId}`);
                    return;
                case "complete-job":
                    if (action.enabled) setShowCompletionSummary(true);
                    return;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [advanceStatus, navigate, jobId, isHelper, leftOpen],
    );

    if (detail.isLoading) return <PageSpinner />;
    if (detail.isError || !detail.data || !job) {
        // Surface a reason the tech can act on instead of one generic line.
        // A non-ApiError (e.g. fetch threw) has no status -> treat as a
        // connectivity problem and offer Retry.
        const status = detail.error instanceof ApiError ? detail.error.status : undefined;
        let message: string;
        let action: "home" | "login" | "retry";
        if (status === 401) {
            message = "Your session has expired. Please log in again.";
            action = "login";
        } else if (status === 404) {
            message = "This job could not be found. It may have been cancelled or removed.";
            action = "home";
        } else if (status === 400) {
            message = "This job link is invalid. Return to your job list.";
            action = "home";
        } else {
            message = "Could not connect to the server. Check your connection and try again.";
            action = "retry";
        }
        return (
            <div className="max-w-2xl mx-auto py-8 text-center space-y-3">
                <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
                <p className="text-sm text-shell-foreground/80">{message}</p>
                {action === "login" ? (
                    <Button variant="outline" onClick={() => navigate("/login")}>
                        <LogIn className="h-4 w-4 mr-1" /> Log In
                    </Button>
                ) : action === "retry" ? (
                    <Button variant="outline" onClick={() => void detail.refetch()}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Retry
                    </Button>
                ) : (
                    <Button variant="outline" onClick={goHome}>
                        <ArrowLeft className="h-4 w-4 mr-1" /> Home
                    </Button>
                )}
            </div>
        );
    }

    const mapsUrl = buildMapsUrl(job);
    const address = buildAddress(job);
    const canWork = job.status === "in_progress";
    const canAddPhotos = job.status === "in_progress" || job.status === "office_review";
    const contactName = [job.contact_first_name, job.contact_last_name].filter(Boolean).join(" ");
    const contactPhone = job.contact_phone || job.customer_phone;

    // ── Get There call options: "Call site" = the RESOLVED on-site contact
    //    (contact-card endpoint), legacy numbers offered when they differ
    //    (deduped on digits). ──
    const callOptions = buildCallOptions({
        resolvedOnSitePhone: contactCardQ.data?.on_site?.phone ?? null,
        briefingSiteContactPhone: briefing.site_contact_phone ?? null,
        jobSiteContactPhone: job.site_contact_phone ?? null,
        sitePhone: job.site_phone ?? null,
        customerPhone: contactPhone ?? null,
    });

    /**
     * ONE title source, used by BOTH the composed title and the office-note
     * suppression below.
     *
     * #1059 fixed the duplicate note (see #1063) by asking "does the office's wording add
     * a word we aren't already showing" — which only works if the helper is
     * told what IS showing. This page used to call it with just the title and
     * job type, so the customer name was invisible to the check and
     * `Note from the office: "Backflow Test — Oak Ridge Dental"` still printed
     * under a header already reading "Oak Ridge Dental / Backflow Test". Same
     * rule, half the inputs. Building the source once removes the chance of
     * the two calls disagreeing again.
     */
    const titleSource = {
        title: job.title,
        job_type_name: job.job_type_name,
        // This page carries trade_name (not trade_code); composeJobTitle
        // falls back to it directly.
        trade_name: job.trade_name,
        customer_name: job.company_name ?? null,
        site_name: job.site_name ?? job.site_label ?? null,
        site_city: job.site_city ?? null,
        device_count: totalAssets,
    };

    // ── Reference column models — every fact's single home ──
    const woDetails = woDetailsQ.data ?? null;
    const scopeOfWork = briefing.scope_of_work || woDetails?.scopeOfWork || null;
    const referenceBriefing: ReferenceBriefing | null = isVendorWO
        ? {
              workOrderNumber: briefing.work_order_number ?? null,
              scopeOfWork: techPlan ? null : scopeOfWork,
              specialInstructions: techPlan ? null : (briefing.special_instructions ?? null),
              trackingNumber: briefing.tracking_number ?? null,
              siteContactName: briefing.site_contact_name ?? null,
              siteContactPhone: briefing.site_contact_phone ?? null,
          }
        : null;
    // Amber scheduling / tech notes: always visible in the reference column
    // and mirrored inside Get There while it can still change behavior.
    // With "What you'll do today" loaded, the WO brief shows ONCE — behind its
    // "Read the work order" — not here and again in Get There.
    const warningText = isVendorWO && !techPlan ? (woDetails?.techNotes ?? null) : null;
    // Non-WO jobs: office notes for the tech render once, here. (Vendor WOs
    // carry the same string inside the briefing scope — never render both.)
    // The thread replaces the single string whenever it has entries; the
    // string stays as the fallback (tech_instructions-only jobs, thread
    // still loading).
    // Older repair jobs carry the quote's lines pasted in as a system note
    // ("Scope (from estimate N): …" — prices, part numbers, fees). Once the
    // job has its plain scope lines up top, that note is history, not tech
    // reading: it stays in the office thread and drops off the iPad.
    const hidePastedQuote = (body: string | null | undefined, authorName?: string | null) =>
        !!job.summary?.trim() && !authorName && isPastedQuoteNote(body);
    // tech_instructions moved into "What you'll do today" (2026-10-22).
    const rawTechNotes = !isVendorWO ? job.notes || (techPlan ? null : job.tech_instructions) || null : null;
    const techNotes = hidePastedQuote(rawTechNotes) ? null : rawTechNotes;
    const notesThread = !isVendorWO
        ? (jobNotesQ.data?.notes ?? [])
              .filter((n) => !hidePastedQuote(n.body, n.author_name))
              .map((n) => ({
                  id: n.id,
                  authorName: n.author_name,
                  body: n.body,
                  pinned: n.pinned,
                  createdAt: n.created_at,
              }))
        : [];

    const rawDeviceStatuses = deriveDeviceStatuses(
        assets.map((a) => ({
            asset_id: a.asset_id,
            assembly_type: a.assembly_type,
            parent_asset_id: a.parent_asset_id,
            disposition: a.disposition,
            required_action: a.required_action,
        })),
        isFireSafety
            ? (fsResults.data?.results ?? []).map((r) => ({ asset_id: r.asset_id, result: r.result }))
            : (bfResults.data?.results ?? []).map((r) => ({ asset_id: r.asset_id, result: r.overall_result })),
    );
    // Helper Mode v2 honest state: on the helper's screen a device with saved
    // readings reads ENTERED (waiting on the tech), never pass/fail-complete.
    const deviceStatuses = isHelper ? helperVisibleStatuses(rawDeviceStatuses) : rawDeviceStatuses;
    // #948 J5 — devices whose two recorded locations disagree and nobody has
    // settled it. The server derives the flag; we just surface the ones it
    // marked, so the card can never outlive the conflict.
    const locationConflicts = assets
        .filter((a) => a.location_conflict === true)
        .map((a) => ({
            assetId: a.asset_id,
            label: a.label || a.serial_number || "This device",
            deviceLocation: a.location_description,
            workOrderLocation: a.wo_reported_location ?? null,
        }));
    const referenceDevices: ReferenceDevice[] = assets.map((a) => ({
        linkId: a.link_id,
        assetId: a.asset_id,
        label: a.label || a.serial_number || "Device",
        subline: [a.manufacturer, a.model, a.size_label].filter(Boolean).join(" · ") || null,
        serial: a.serial_number,
        location: a.location_description,
        assetType: a.asset_type,
        requiredAction: a.required_action,
        status: deviceStatuses.get(a.asset_id) ?? "not_tested",
    }));
    const deviceTapTarget = isBackflow
        ? `/field/backflow/${job.id}`
        : isFireSafety
          ? `/field/fire-safety/${job.id}`
          : null;

    const scheduleLine = job.scheduled_start
        ? `${formatDate(job.scheduled_start)} · ${formatTime(job.scheduled_start)}${
              job.scheduled_end ? ` – ${formatTime(job.scheduled_end)}` : ""
          }`
        : null;

    // §3b: the sticky bottom button is the STANDARD job lifecycle control —
    // identical on every job type, never a WO step action. Step actions
    // render inline in their step cards in the rail.
    const lifecycle = engine.lifecycleAction;
    const LifecycleIcon = lifecycle ? ACTION_ICONS[lifecycle.kind] : null;
    const lifecycleDisabled = advanceStatus.isPending || (lifecycle?.kind === "complete-job" && !lifecycle.enabled);

    // Slim progress strip above the lifecycle button while the workflow is
    // still advancing — keeps the flow visible without owning the button.
    const activeStep = engine.steps.find((s) => s.id === engine.activeStepId) ?? null;
    const progressStrip =
        job.status === "in_progress" && activeStep && activeStep.id !== "wrap-up"
            ? {
                  text: `${isVendorWO ? "Work order: " : ""}step ${activeStep.ordinal} of ${engine.steps.length} — ${activeStep.title}`,
                  targetId: `step-${activeStep.id}`,
              }
            : null;

    return (
        <div className="mx-auto max-w-6xl pb-32 space-y-4">
            {/* #1512 — the tech submitted, the office doesn't have it yet, and
                he is about to be offered Complete Job anyway. Say so, or the
                unlocked button looks like the app forgot what he just did. */}
            {submitQueuedHere && (
                <div
                    data-testid="submit-sending-note"
                    className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200"
                >
                    <span className="font-medium">Your tests are on their way to the office.</span> They're saved on
                    this phone and send themselves — carry on and tap Complete Job when the rest of the work is done.
                </div>
            )}

            {/* "No work order yet" (2026-10-21): before he heads out, in plain
                words. Cintas #092678170 was signed with nothing to sign. */}
            {job.wo_missing && !isJobDoneForTech(job.status) && (
                <div
                    role="alert"
                    data-testid="field-no-work-order"
                    className="flex items-start gap-3 rounded-xl border-2 border-red-400 bg-red-50 p-4 text-red-950 dark:border-red-500/60 dark:bg-red-500/15 dark:text-red-50"
                >
                    <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-red-600 dark:text-red-400" aria-hidden />
                    <div>
                        <p className="text-base font-bold">No work order yet</p>
                        <p className="text-sm">No work order on this job yet. Call the office before you start.</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between gap-2 -mt-1">
                <button
                    onClick={goHome}
                    className="flex items-center gap-1.5 text-sm text-shell-foreground/70 hover:text-shell-foreground transition-colors -ml-1 px-1 py-1"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Home
                </button>
                <Badge
                    className={cn(
                        statusColor(job.status),
                        job.status === "cannot_complete" && "bg-destructive text-destructive-foreground",
                    )}
                    variant="outline"
                >
                    {job.status?.replace(/_/g, " ")}
                </Badge>
            </div>

            <div className="space-y-1">
                <span className="text-xs font-mono text-shell-foreground/70 block">
                    Job {job.display_number ? `#${job.display_number}` : "— not numbered"}
                    {briefing.work_order_number ? ` · WO ${briefing.work_order_number}` : ""}
                </span>
                {/* #948 J12 — the status pill is system vocabulary
                    ("office_review"). Doctrine 4 says the explanation lives ON
                    the screen, not in a manual a green hire will never open,
                    so the plain meaning renders right under the header rather
                    than hiding in a tooltip a gloved thumb can't reach. */}
                {redesignEnabled && statusMeaning(job.status) && (
                    <p className="text-xs text-shell-foreground/70 leading-snug" data-testid="job-status-meaning">
                        {statusMeaning(job.status)}
                    </p>
                )}
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground leading-tight">
                    {job.company_name || "Customer"}
                </h1>
                {/* #948 J1/J3 — the same plain-title contract as the home
                    screen (#947): a composed title a green hire can act on,
                    with the office's original wording kept but demoted to a
                    labeled line instead of BEING the headline. */}
                <p className="text-base text-shell-foreground/80 leading-tight" data-testid="job-title">
                    {redesignEnabled ? composeJobTitle(titleSource) : job.title}
                </p>
                {/* "Note from the office: <title>" is gone (2026-10-22): the
                    office's words for the tech live in "What you'll do today". */}
                {/* #948 J11 — "done means done" (doctrine 3). A finished job
                    used to end in a bare "Nothing to do here." footer at the
                    very bottom, so a tech scrolling a completed job still met
                    live-looking controls and had to guess whether the office
                    wanted anything. The receipt states it up top, and says
                    what the OFFICE does next so `office_review` isn't a
                    mystery status the tech meets with no explanation. */}
                {redesignEnabled &&
                    isJobDoneForTech(job.status) &&
                    (() => {
                        const receipt = doneReceipt(job.status);
                        if (!receipt) return null;
                        return (
                            <div
                                className="mt-2 rounded-lg border border-green-600/40 bg-green-50 p-3 dark:bg-green-950/40"
                                data-testid="job-done-receipt"
                            >
                                <p className="text-sm font-bold text-green-800 dark:text-green-200">
                                    ✓ {receipt.headline}
                                </p>
                                <p className="mt-1 text-xs leading-snug text-green-900/80 dark:text-green-100/80">
                                    {receipt.officeNext}
                                </p>
                            </div>
                        );
                    })()}
                {/* Vitals row (2026-07-14): PO/WO · tap-to-call location phone ·
                    water account — pinned in the header, no click-hunting. */}
                {(() => {
                    const poRef = job.customer_po?.trim();
                    const woRef = customerWoNumber(
                        {
                            external_ref: job.external_ref,
                            // Live briefing (if loaded) wins over the row's
                            // denormalized copy; both are parse-time snapshots.
                            briefing_wo_number: briefing.work_order_number?.trim() || job.briefing_wo_number,
                        },
                        { includePo: false },
                    );
                    const vitalsPhone =
                        briefing.site_contact_phone?.trim() ||
                        job.site_contact_phone?.trim() ||
                        job.site_phone?.trim() ||
                        job.customer_phone?.trim();
                    const acctNo = job.site_water_account_number?.trim();
                    const hasProvider = !!(job.water_provider_name || job.water_provider_display_name);
                    if (!poRef && !woRef && !vitalsPhone && !hasProvider) return null;
                    return (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-sm font-medium text-shell-foreground/90">
                            {poRef && <span className="tabular-nums">PO {poRef}</span>}
                            {woRef && !briefing.work_order_number && <span className="tabular-nums">WO {woRef}</span>}
                            {vitalsPhone && (
                                <a
                                    href={`tel:${vitalsPhone}`}
                                    className="inline-flex items-center gap-1 rounded-lg bg-primary/15 px-2 py-1 tabular-nums text-primary"
                                    title="Call the location contact"
                                >
                                    <Phone className="h-3.5 w-3.5" /> {vitalsPhone}
                                </a>
                            )}
                            {hasProvider &&
                                // Portal-first: paper/offline jurisdictions have no portal account,
                                // so a stored number is stale/legacy — never show it. Only portals
                                // surface the account (or the amber "missing" nudge).
                                (!job.water_provider_is_portal ? (
                                    <span className="text-shell-foreground/60">no acct — paper</span>
                                ) : acctNo ? (
                                    <span className="tabular-nums" title="Water provider account #">
                                        Acct {acctNo}
                                    </span>
                                ) : (
                                    <span className="rounded bg-amber-400/20 px-1.5 py-0.5 text-xs font-bold uppercase text-amber-600 dark:text-amber-300">
                                        acct missing
                                    </span>
                                ))}
                        </div>
                    );
                })()}
                {job.job_type_name && (
                    <div className="pt-2">
                        <span className="inline-flex items-center gap-2 rounded-lg bg-primary/15 text-primary px-3 py-1.5 text-sm font-bold tracking-wide uppercase">
                            <Briefcase className="h-4 w-4" />
                            {redesignEnabled ? plainJobTypeName(job.job_type_name) : job.job_type_name}
                        </span>
                    </div>
                )}
            </div>

            {/* THE WORK — the office's brief / the quote's device lines, first
                and big (2026-10-15). Not repeated in the reference column. */}
            {/* Add from the field, pick 1: location notes at the top of every
                visit, above the work; customer notes as one "tap to read" line. */}
            {fieldAdd && (
                <FieldAddLocationCard
                    items={fieldAdd.location ?? []}
                    onOpenFile={(i) => setPreviewAttachment({ id: i.id, fileName: i.fileName ?? "File" })}
                />
            )}
            {/* threads/job-page-fresh: the same top box the office reads ("20 passed · 2 failed"). */}
            <JobOutcomeBox jobId={job.id} resp={detail.data} preVisit={phaseOf(job.status) === "before"} />
            {techPlan ? (
                <TechPlanBlock plan={techPlan} readAt={planReadAt} />
            ) : (
                <ScopeLinesCard summary={job.summary} />
            )}
            {/* Read your plan (10/28): Got it / This looks wrong, right under
                the plan he reads first. The office sees who read it. */}
            {techPlan && techPlan.lines.length > 0 && <ReadYourPlan jobId={job.id} />}
            {fieldAdd && (
                <FieldAddCustomerLine
                    items={fieldAdd.customer ?? []}
                    onOpenFile={(i) => setPreviewAttachment({ id: i.id, fileName: i.fileName ?? "File" })}
                />
            )}

            {/* Cannot complete reason (if flagged) */}
            {job.status === "cannot_complete" && job.cannot_complete_reason && (
                <Card className="border-red-500/50 bg-red-50 dark:bg-red-500/10">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-2">
                            <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                            <div>
                                <div className="text-sm font-semibold text-red-700 dark:text-red-400">
                                    Cannot Complete
                                </div>
                                <div className="text-sm mt-1">{job.cannot_complete_reason}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* ── Tabs: Job (default) | Site History (§3e) ── */}
            <div className="flex gap-1 rounded-xl bg-muted/60 p-1">
                {(
                    [
                        { id: "job", label: "Job" },
                        { id: "history", label: "Site History" },
                    ] as const
                ).map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                            "flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                            activeTab === tab.id
                                ? "bg-card text-foreground shadow-sm"
                                : "text-shell-foreground/70 hover:text-shell-foreground",
                        )}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === "history" && <SiteHistoryTab jobId={jobId!} />}

            {/* ── DO (workflow rail) + KNOW (reference) — rail first on phone ── */}
            <div className={cn("grid items-start gap-4 lg:grid-cols-5", activeTab !== "job" && "hidden")}>
                {/* Job Files is FIRST in the DOM on purpose (Issue #719). Below
                    `lg` there is no grid, so children stack in source order —
                    anything placed inside the reference column lands ~3 screens
                    down on a phone, which is how the original work order went
                    missing. The explicit column/row placement pins it to the top
                    of the reference column on iPad without moving the rail. */}
                <JobFilesCard
                    redesign={redesignEnabled}
                    className="lg:col-span-2 lg:col-start-4 lg:row-start-1"
                    files={jobFiles}
                    serviceTitanFiles={paperwork.data?.serviceTitanFiles ?? []}
                    originalWorkOrderMissing={paperwork.data?.originalWorkOrderMissing ?? false}
                    isLoading={paperwork.isLoading}
                    isError={paperwork.isError}
                    onRetry={() => void paperwork.refetch()}
                    isOnline={isOnline}
                    offlineReadyIds={offlineReadyIds}
                    newFileIds={newFileIds}
                    onPreview={(f) => setPreviewAttachment({ id: f.id, fileName: f.fileName })}
                    onDownload={(f) => {
                        void downloadAttachmentFile(f.id, f.fileName).catch(() => toast.error("Download failed"));
                    }}
                    onOpenPhoto={(f) => {
                        const idx = jobPhotos.findIndex((p) => p.id === f.id);
                        setLightboxIndex(idx >= 0 ? idx : 0);
                    }}
                    onAddFiles={canAddPhotos && !isHelper ? (picked) => uploadFiles.mutate(picked) : null}
                    isUploading={uploadFiles.isPending}
                    onDownloadAll={
                        isOnline && jobFiles.length > 1 ? () => downloadAll.mutate(jobFiles.map((f) => f.id)) : null
                    }
                    isDownloadingAll={downloadAll.isPending}
                    onMessageOffice={() => navigate(`/field/chat?jobId=${job.id}`)}
                />

                <div className="min-w-0 space-y-4 lg:col-span-3 lg:col-start-1 lg:row-start-1 lg:row-span-2">
                    <WorkflowRail
                        steps={engine.steps}
                        onAction={handleAction}
                        busy={advanceStatus.isPending}
                        address={address || null}
                        mapsUrl={mapsUrl}
                        onNavigate={() => mapsUrl && openExternalLink(mapsUrl)}
                        scheduleLine={scheduleLine}
                        callOptions={callOptions}
                        warningText={warningText}
                        ivrPhone={briefing.ivr_phone || woDetails?.ivrPhone || null}
                        ivrPin={briefing.ivr_pin ?? null}
                    />

                    {/* Portal entry — per-device wizard for online-reporting
                        purveyors. Shows once the backflow job is submitted; walks
                        the tech through each device's portal fields and its
                        stamped photos. */}
                    {isBackflow &&
                        job.water_provider_is_portal === true &&
                        ["office_review", "completed", "invoiced", "closed"].includes(job.status ?? "") && (
                            <PortalEntryWizard jobId={jobId!} />
                        )}
                </div>

                <div className="min-w-0 space-y-4 lg:col-span-2 lg:col-start-4 lg:row-start-2">
                    {/* Contacts (Prompt E): on-site person + coordinator + office
                        line, two people max — resolved by the contact-card
                        endpoint, same person "Call site" dials. */}
                    <ContactsCard jobId={jobId!} />
                    {/* Parts Custody: parts attached to this job + [I have it].
                        #796 — jobStatus turns it into a read-only receipt once the
                        job is off the tech's hands, so it stops contradicting the
                        "nothing to do here" banner below. */}
                    <PartsForJobCard jobId={jobId!} jobStatus={job.status ?? null} />
                    {/* §7 item 3 — once the §3f wrap-up disposition is answered,
                        the on-site send action stays on the job screen: a fresh
                        [Send report copy] send is always possible (Plano), with
                        a new Activity line per send. */}
                    {reportCopyRequired && reportDisposition && jobId && (
                        <OnsiteReportCopySection
                            jobId={jobId}
                            providerLabel={
                                job?.water_provider_display_name || job?.water_provider_name || "This jurisdiction"
                            }
                            smsEnabled={detail.data?.onsiteReportSmsEnabled === true}
                            canIncludeWorkOrder={isVendorWO && hasCompletedWorkOrder}
                            disposition={reportDisposition}
                            onRecorded={(d) => {
                                setRecordedReportDisposition(d);
                                qc.invalidateQueries({ queryKey: ["field", "job-detail", jobId] });
                            }}
                        />
                    )}
                    {/* #948 J5 — one amber card per unsettled location
                        conflict, above the reference column so the tech meets
                        it before they read a location and walk. */}
                    {redesignEnabled &&
                        jobId &&
                        locationConflicts.map((d) => (
                            <LocationTruthCard
                                key={d.assetId}
                                jobId={jobId}
                                device={d}
                                onSettled={() => qc.invalidateQueries({ queryKey: ["field", "job-detail", jobId] })}
                            />
                        ))}
                    <ReferenceColumn
                        redesign={redesignEnabled}
                        scopeActions={assets.map((a) => a.required_action)}
                        scopeDeviceCount={assets.length}
                        briefing={referenceBriefing}
                        warningText={warningText}
                        techNotes={techNotes}
                        notesThread={notesThread}
                        onAddNote={isHelper ? null : () => setShowAddNote(true)}
                        devices={referenceDevices}
                        onDeviceTap={deviceTapTarget && canWork ? () => navigate(deviceTapTarget) : null}
                        location={{
                            siteName: job.site_name || job.site_label,
                            address: address || null,
                            contactName: contactName || null,
                            contactRole: job.contact_role,
                            providerName: job.water_provider_name
                                ? job.water_provider_display_name || job.water_provider_name
                                : null,
                            providerPlatform: job.water_provider_submission_platform ?? null,
                            providerIsPortal: job.water_provider_is_portal ?? null,
                            providerPortalUrl: job.water_provider_portal_url ?? null,
                            // Canonical portal account only — the legacy ST
                            // code never renders in a portal-account slot.
                            accountNumber: job.site_water_account_number ?? null,
                        }}
                        jobId={job.id}
                        unreadMessages={chatUnread.data?.total ?? 0}
                        onOpenMessages={() => navigate(`/field/chat?jobId=${job.id}`)}
                        onManualPdf={isVendorWO ? () => navigate(`/field/work-order-pdf/${jobId}`) : null}
                        renderDeviceExtra={
                            fieldAdd
                                ? (assetId) => (
                                      <FieldAddDeviceExtras
                                          items={(fieldAdd.devicesNotes ?? []).filter((i) => i.assetId === assetId)}
                                          onAdd={
                                              fieldAdd.isHelper
                                                  ? null
                                                  : () => {
                                                        setFieldAddPreset(assetId);
                                                        setFieldAddOpen(true);
                                                    }
                                          }
                                          onOpenFile={(i) =>
                                              setPreviewAttachment({ id: i.id, fileName: i.fileName ?? "File" })
                                          }
                                      />
                                  )
                                : null
                        }
                    />
                </div>
            </div>

            {/* Bottom action bar — the STANDARD job lifecycle control (§3b),
                identical on every job type. WO step actions live inline in
                their step cards, never here. */}
            <div className="fixed bottom-0 inset-x-0 z-30 border-t border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/80">
                {progressStrip && (
                    <button
                        type="button"
                        onClick={() =>
                            document
                                .getElementById(progressStrip.targetId)
                                ?.scrollIntoView({ behavior: "smooth", block: "center" })
                        }
                        className="block w-full border-b border-border/60 bg-muted/50 px-4 py-1.5 text-center text-xs text-foreground/70"
                    >
                        {progressStrip.text}
                        <ChevronUp className="ml-1 inline h-3.5 w-3.5 align-[-2px]" />
                    </button>
                )}
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2">
                    {isHelper ? (
                        <div className="flex-1 flex flex-col gap-1">
                            {helperClockedIn && (
                                <p className="text-xs text-green-700 dark:text-green-400 text-center font-medium">
                                    Clocked in at {helperSession ? formatTime(helperSession.clockInAt) : ""} — your work
                                    saves for the tech to review
                                </p>
                            )}
                            <Button
                                size="lg"
                                className={cn(
                                    "h-12 w-full text-base font-semibold",
                                    helperClockedIn
                                        ? "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                                        : "bg-green-700 text-white hover:bg-green-800",
                                )}
                                onClick={() => helperClock.mutate(helperClockedIn ? "out" : "in")}
                                disabled={helperClock.isPending || helperSessionQ.isLoading}
                            >
                                {helperClock.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <LogIn className="h-5 w-5 mr-2" />
                                )}
                                {helperClockedIn ? "Clock Out" : "Clock In — I'm on site"}
                            </Button>
                        </div>
                    ) : lifecycle ? (
                        <>
                            {job.status === "in_progress" && (
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className="h-12"
                                    onClick={() => setShowCannotComplete(true)}
                                >
                                    <Undo2 className="h-4 w-4 mr-1" />
                                    Return needed
                                </Button>
                            )}
                            <div className="flex-1 flex flex-col gap-1">
                                {lifecycle.kind === "complete-job" && !lifecycle.enabled && (
                                    <p className="text-xs text-amber-600 text-center font-medium">
                                        {isRepairJob && !repairComplete
                                            ? `Repaired ${repairedCount}/${repairCount} · Tested ${testedCount}/${testableCount}${replaceCount > 0 ? ` · Replaced ${replacedCount}/${replaceCount}` : ""} — complete all workflows`
                                            : replaceCount > 0 && !replaceComplete
                                              ? `Tested ${testedCount}/${testableCount} · Replaced ${replacedCount}/${replaceCount} — complete all workflows`
                                              : !testComplete
                                                ? `${testedCount} / ${testableCount} devices resolved — test or skip the rest first`
                                                : helperUnreviewedCount > 0
                                                  ? `${helperUnreviewedCount} device${helperUnreviewedCount === 1 ? "" : "s"} entered by ${helperEnteredByName} — review (confirm or edit) each one to unlock Complete Job`
                                                  : !reportSubmitted
                                                    ? isFireSafety
                                                        ? "Submit the inspection report (Review & Submit) to unlock Complete Job"
                                                        : "Submit the test report (Review & Submit) to unlock Complete Job"
                                                    : !woFormSubmitted
                                                      ? "Submit the work order form to unlock Complete Job"
                                                      : "Record the check-in and check-out on the vendor's automated phone line (IVR) to unlock Complete Job"}
                                    </p>
                                )}
                                <Button
                                    size="lg"
                                    className={cn(
                                        "h-12 w-full text-base font-semibold",
                                        lifecycle.kind === "complete-job" &&
                                            lifecycle.enabled &&
                                            "bg-green-700 text-white hover:bg-green-800",
                                    )}
                                    onClick={() => handleAction(lifecycle)}
                                    disabled={lifecycleDisabled}
                                >
                                    {advanceStatus.isPending ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        LifecycleIcon && <LifecycleIcon className="h-5 w-5 mr-2" />
                                    )}
                                    {lifecycle.label}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 text-center text-sm text-foreground/80 py-2">
                            {engine.footer ?? "Nothing to do here."}
                        </div>
                    )}
                </div>
            </div>

            {/* IVR confirmation dialog */}
            {ivrDialog && (
                <Dialog
                    open
                    onClose={() => setIvrDialog(null)}
                    title={ivrDialog.action === "checkin" ? "IVR check-in" : "IVR check-out"}
                    className="max-w-sm"
                >
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <label className="text-sm font-medium">Confirmation number from the IVR (optional)</label>
                            <Input
                                autoFocus
                                value={ivrConfirmation}
                                onChange={(e) => setIvrConfirmation(e.target.value)}
                                placeholder="Leave blank if none"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setIvrDialog(null)}>
                                Cancel
                            </Button>
                            <Button
                                disabled={ivrCheckin.isPending || ivrCheckout.isPending}
                                onClick={() => {
                                    const value = ivrConfirmation.trim() ? ivrConfirmation.trim() : null;
                                    if (ivrDialog.action === "checkin") {
                                        ivrCheckin.mutate(value);
                                    } else {
                                        ivrCheckout.mutate(value);
                                    }
                                    setIvrDialog(null);
                                }}
                            >
                                Record {ivrDialog.action === "checkin" ? "check-in" : "check-out"}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            )}

            {/* Photo lightbox — the SAME touch viewer Site History uses (swipe,
                pinch-zoom, double-tap, pull-to-close). This used to be a
                hand-rolled overlay with none of that, so the Job tab and the
                History tab behaved differently on the same photos. Retake and
                Delete ride in the footer slot so they stay thumb-reachable. */}
            {lightboxPhoto && (
                <PhotoLightbox
                    open
                    index={lightboxIndex ?? 0}
                    onIndexChange={setLightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                    photos={jobPhotos.map((p) => ({
                        src: attachmentDownloadUrl(p.id, { disposition: "inline" }),
                        caption: p.photoTag ?? p.fileName,
                    }))}
                    actions={
                        canAddPhotos && !isHelper ? (
                            <>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={retakePhoto.isPending || deletePhoto.isPending}
                                    onClick={() => {
                                        setRetakeTarget(lightboxPhoto);
                                        setLightboxIndex(null);
                                    }}
                                >
                                    <Camera className="mr-2 h-4 w-4" />
                                    Retake
                                </Button>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    disabled={retakePhoto.isPending || deletePhoto.isPending}
                                    onClick={() => deletePhoto.mutate(lightboxPhoto.id)}
                                >
                                    {deletePhoto.isPending ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <Trash2 className="mr-2 h-4 w-4" />
                                    )}
                                    Delete
                                </Button>
                            </>
                        ) : null
                    }
                />
            )}

            {/* Tech one-tap flag — friction surfaces itself, no typing. */}
            <div className="flex justify-center">
                <FlagProblemButton jobId={jobId ?? null} />
            </div>

            {/* Parts Custody disposition gate — "where's the part?" */}
            <PartsDispositionDialog
                open={partsGate !== null}
                parts={partsGate?.parts ?? []}
                busy={returnNeeded.isPending || submitCompletion.isPending}
                onCancel={() => setPartsGate(null)}
                onSubmit={(answers) => partsGate?.retry(answers)}
            />

            {/* Return-needed dialog (Issue #454) — non-terminal; the office decides
                the job's fate. A tech never marks the whole job cannot-complete. */}
            <Dialog open={showCannotComplete} onClose={() => setShowCannotComplete(false)} title="Return needed">
                <div className="space-y-4">
                    <p className="text-sm text-foreground/80">
                        This ends today's trip and sends the job to the office to reschedule. Tell them what happened.
                    </p>
                    <Textarea
                        value={cannotCompleteReason}
                        onChange={(e) => setCannotCompleteReason(e.target.value)}
                        placeholder="Need a return trip: parts on order, ran out of day, call before next visit…"
                        rows={4}
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setShowCannotComplete(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() =>
                                returnNeeded.mutate({ reason: cannotCompleteReason, kind: "couldnt_finish" })
                            }
                            disabled={returnNeeded.isPending || cannotCompleteReason.trim().length === 0}
                        >
                            {returnNeeded.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Undo2 className="h-4 w-4 mr-2" />
                            )}
                            Send to office
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Completion dialog (§3c): the checklist is DERIVED from the
                device/test data the rail already computes — the tech reviews
                it instead of attesting via manual checkboxes. */}
            <Dialog
                open={showCompletionSummary}
                onClose={() => setShowCompletionSummary(false)}
                title="Wrap up the job"
            >
                <div className="space-y-4">
                    {!isGeneralWorkflow && (
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                                {testComplete ? (
                                    <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                                ) : (
                                    <XCircle className="h-4 w-4 shrink-0 text-amber-600" />
                                )}
                                <span>
                                    {testableCount === 0
                                        ? "No devices to inspect on this job"
                                        : `Inspections: ${testedCount}/${testableCount} devices done`}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                {jobPhotos.length > 0 ? (
                                    <CheckCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                                ) : (
                                    <StickyNote className="h-4 w-4 shrink-0 text-foreground/50" />
                                )}
                                <span>
                                    {jobPhotos.length > 0
                                        ? `${jobPhotos.length} photo${jobPhotos.length === 1 ? "" : "s"} attached`
                                        : "No photos attached (ok if not needed)"}
                                </span>
                            </div>
                        </div>
                    )}
                    {isGeneralWorkflow && (
                        <p className="text-xs text-foreground/80">
                            Send this job to office review when the work is done.
                        </p>
                    )}
                    {reportCopyRequired && jobId && (
                        <OnsiteReportCopySection
                            jobId={jobId}
                            providerLabel={
                                job?.water_provider_display_name || job?.water_provider_name || "This jurisdiction"
                            }
                            smsEnabled={detail.data?.onsiteReportSmsEnabled === true}
                            canIncludeWorkOrder={isVendorWO && hasCompletedWorkOrder}
                            disposition={reportDisposition}
                            onRecorded={(d) => {
                                setRecordedReportDisposition(d);
                                qc.invalidateQueries({ queryKey: ["field", "job-detail", jobId] });
                            }}
                        />
                    )}
                    <p className="text-xs text-foreground/70 flex items-start gap-1.5">
                        <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                            Need to flag something for the office? Add it under Notes on the job screen — you can jot
                            notes any time, not just here.
                        </span>
                    </p>
                    {!isGeneralWorkflow && !testComplete && (
                        <p className="text-xs text-amber-600">
                            Finish or release the remaining devices before completing the job.
                        </p>
                    )}
                    {reportCopyRequired && !reportDisposition && (
                        <p className="text-xs text-amber-600">
                            Answer the on-site report copy question above before completing the job.
                        </p>
                    )}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button variant="ghost" onClick={() => setShowCompletionSummary(false)}>
                            Not yet
                        </Button>
                        <Button
                            onClick={() => submitCompletion.mutate(undefined)}
                            disabled={
                                submitCompletion.isPending ||
                                (!isGeneralWorkflow && !testComplete) ||
                                (reportCopyRequired && !reportDisposition)
                            }
                        >
                            {submitCompletion.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle className="h-4 w-4 mr-2" />
                            )}
                            Mark complete
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Add note (Issue #282): a tech drops a general note into the job
                notes thread any time during the job. */}
            <Dialog
                open={showAddNote}
                onClose={() => {
                    setShowAddNote(false);
                    setNoteDraft("");
                }}
                title="Add a note"
            >
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium flex items-center gap-1.5">
                            <StickyNote className="h-3.5 w-3.5" />
                            Note for the office
                        </label>
                        <Textarea
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            placeholder="Anything the office should know about this job…"
                            rows={5}
                            autoFocus
                        />
                        <p className="text-xs text-foreground/60">
                            Saved to this job's notes — visible to the office and on the job screen.
                        </p>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setShowAddNote(false);
                                setNoteDraft("");
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={() => addNote.mutate()} disabled={addNote.isPending || !noteDraft.trim()}>
                            {addNote.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <StickyNote className="h-4 w-4 mr-2" />
                            )}
                            Save note
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={showJobPhotos || retakeTarget !== null}
                onClose={() => {
                    setShowJobPhotos(false);
                    setRetakeTarget(null);
                }}
                title={retakeTarget ? "Retake Photo" : "Add Photo"}
            >
                <JobPhotoChooser
                    // Job photos are a gallery → multi-select; a retake
                    // replaces ONE specific photo → single.
                    multi={!retakeTarget}
                    onPhoto={(dataUrl: string) => {
                        if (retakeTarget) {
                            retakePhoto.mutate({ attachment: retakeTarget, dataUrl });
                        } else {
                            uploadPhoto.mutate({ dataUrl });
                        }
                        setShowJobPhotos(false);
                    }}
                    onCancel={() => {
                        setShowJobPhotos(false);
                        setRetakeTarget(null);
                    }}
                />
            </Dialog>

            <LeavingSiteCloseoutModal
                open={closeoutOpen}
                trigger={closeoutTrigger}
                officeNote={job?.pending_closeout_note ?? null}
                officeNoteFromName={job?.pending_closeout_by_name ?? null}
                busy={returnNeeded.isPending || advanceStatus.isPending}
                onResolve={handleCloseoutResolve}
                onDismiss={handleCloseoutDismiss}
            />

            <AttachmentPreviewDialog
                open={previewAttachment != null}
                onClose={() => setPreviewAttachment(null)}
                attachmentId={previewAttachment?.id ?? null}
                fileName={previewAttachment?.fileName}
            />

            {jobId && fieldAdd && (
                <>
                    {!fieldAdd.isHelper && <TeachIntro featureKey="field-add.ipad" when />}
                    <FieldAddButton
                        onClick={() => {
                            setFieldAddPreset(null);
                            setFieldAddOpen(true);
                        }}
                    />
                    <FieldAddSheet
                        open={fieldAddOpen}
                        onClose={() => setFieldAddOpen(false)}
                        jobId={jobId}
                        view={fieldAdd}
                        isOnline={isOnline}
                        presetAssetId={fieldAddPreset}
                    />
                </>
            )}
            {jobId && <FloatingJobChat jobId={jobId} />}
        </div>
    );
}
